import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { usePromptUploads } from '@/features/voice-campaigns/api';
import type { PromptCategory } from './shared-utils';

interface PromptSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  category: PromptCategory;
  required?: boolean;
}

export function PromptSelect({
  label,
  value,
  onChange,
  category,
  required = false,
}: PromptSelectProps) {
  const prompts = usePromptUploads();
  const options = (prompts.data ?? [])
    .filter(
      (p) =>
        p.promptCategory === category &&
        (p.promptStatus === undefined ||
          Number(p.promptStatus) === 1 ||
          p.promptStatus === '1'),
    )
    .map((p) => ({
      value: String(p.promptId),
      label: p.fileName || p.promptName || `#${p.promptId}`,
    }));

  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="ml-1 text-destructive">*</span>}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue
            placeholder={
              prompts.isLoading
                ? 'Loading…'
                : options.length === 0
                  ? `No ${category} files uploaded`
                  : 'Pick a voice file'
            }
          />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface SmsBlockProps {
  flag: boolean;
  setFlag: (v: boolean) => void;
  url: string;
  setUrl: (v: string) => void;
  label: string;
  description: string;
  smsWebhookFlag: boolean;
  smsWebhookOptions: Array<{ value: string; label: string }>;
}

export function SmsBlock({
  flag,
  setFlag,
  url,
  setUrl,
  label,
  description,
  smsWebhookFlag,
  smsWebhookOptions,
}: SmsBlockProps) {
  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-muted-foreground">{description}</div>
        </div>
        <Switch checked={flag} onCheckedChange={setFlag} />
      </div>
      {flag && (
        <div className="space-y-2">
          {smsWebhookFlag ? (
            <Select value={url} onValueChange={setUrl}>
              <SelectTrigger>
                <SelectValue placeholder="Pick an SMS webhook" />
              </SelectTrigger>
              <SelectContent>
                {smsWebhookOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://sms-gateway.example.com/send"
              className="font-mono text-xs"
            />
          )}
        </div>
      )}
    </div>
  );
}
